import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Modal,
  Paper
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useLocation, useNavigate } from "react-router-dom";
import "./LoginPage.css";

const UpdatePasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";

  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const fetchCSRF = async () => {
    try {
      const r = await fetch("/sistema/get-csrf-token/", {
        method: "GET",
        credentials: "include"
      });
      const d = await r.json();
      return d.csrfToken;
    } catch {
      return null;
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    navigate("/login");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.new_password || !form.confirm_password) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    if (form.new_password !== form.confirm_password) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    const csrf = await fetchCSRF();
    if (!csrf) {
      setError("No se pudo obtener el token CSRF.");
      return;
    }

    try {
      const response = await fetch("/sistema/update-password/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf
        },
        body: JSON.stringify({
          email: email,
          nueva: form.new_password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessOpen(true);
      } else {
        setError(data.error || "Error al actualizar la contraseña.");
      }
    } catch {
      setError("Error de conexión con el servidor.");
    }
  };

  return (
    <div className="login-container">
      <div className="overlay">
        <Container maxWidth="xs" className="login-form">
          <Typography variant="h5" align="center" gutterBottom className="login-title">
            Actualizar Contraseña
          </Typography>

          {error && <Typography className="error-message">{error}</Typography>}

          <form onSubmit={submit}>
            <TextField
              label="Nueva contraseña"
              type={showPassword1 ? "text" : "password"}
              fullWidth
              margin="normal"
              name="new_password"
              value={form.new_password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword1(!showPassword1)}>
                      {showPassword1 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              label="Confirmar contraseña"
              type={showPassword2 ? "text" : "password"}
              fullWidth
              margin="normal"
              name="confirm_password"
              value={form.confirm_password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword2(!showPassword2)}>
                      {showPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button type="submit" variant="contained" fullWidth className="login-button">
              GUARDAR CONTRASEÑA
            </Button>
          </form>
        </Container>
      </div>

      <Modal open={successOpen} onClose={handleSuccessClose}>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            background: "rgba(10, 24, 48, 0.35)",
            backdropFilter: "blur(4px)"
          }}
        >
          <Paper
            sx={{
              width: "100%",
              maxWidth: 460,
              borderRadius: "24px",
              px: 4,
              py: 4,
              textAlign: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%)",
              boxShadow: "0 24px 60px rgba(10, 61, 145, 0.18)",
              border: "1px solid rgba(22, 149, 228, 0.16)"
            }}
          >
            <Box
              sx={{
                width: 78,
                height: 78,
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1695e4 0%, #0a3d91 100%)",
                boxShadow: "0 18px 38px rgba(10, 61, 145, 0.22)"
              }}
            >
              <CheckCircleRoundedIcon sx={{ color: "#fff", fontSize: 42 }} />
            </Box>

            <Typography
              sx={{
                color: "#10233d",
                fontSize: "1.8rem",
                fontWeight: 700,
                lineHeight: 1.15,
                mb: 1.5
              }}
            >
              Contraseña actualizada
            </Typography>

            <Typography
              sx={{
                color: "#5f7185",
                fontSize: "1rem",
                lineHeight: 1.75,
                mb: 3
              }}
            >
              Tu contraseña fue actualizada correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              onClick={handleSuccessClose}
              sx={{
                minHeight: 46,
                borderRadius: "999px",
                background: "linear-gradient(135deg, #1695e4 0%, #0a3d91 100%)",
                textTransform: "none",
                fontSize: "0.98rem",
                fontWeight: 700,
                boxShadow: "0 14px 30px rgba(10, 61, 145, 0.18)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1187cf 0%, #083578 100%)",
                  boxShadow: "0 16px 34px rgba(10, 61, 145, 0.22)"
                }
              }}
            >
              Ir al inicio de sesión
            </Button>
          </Paper>
        </Box>
      </Modal>
    </div>
  );
};

export default UpdatePasswordPage;